import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function makeLast14Days() {
  const arr = [];
  // IST-anchored date iso
  const now = new Date();
  const ist = new Date(now.getTime() + (330 - now.getTimezoneOffset()) * 60000);
  for (let i = 13; i >= 0; i--) {
    const d = new Date(ist);
    d.setUTCDate(ist.getUTCDate() - i);
    arr.push(d.toISOString().slice(0, 10));
  }
  return arr;
}

const encouragements = [
  { min: 0,  msg: "Start your streak today. One study session lights the fire." },
  { min: 1,  msg: "You lit the flame! Keep it going tomorrow — same time?" },
  { min: 3,  msg: "3 days in a row. Habits are forming — don't miss tomorrow." },
  { min: 7,  msg: "A whole week of study! You just outpaced 90% of learners." },
  { min: 14, msg: "Two-week streak! You're officially on a roll. 🔥" },
  { min: 30, msg: "A full month of learning. Legendary." },
];

function encouragementFor(streak) {
  let last = encouragements[0];
  for (const e of encouragements) if (streak >= e.min) last = e;
  return last.msg;
}

export default function StreakStrip() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) { setData(null); return; }
    api.get("/streak").then((r) => setData(r.data)).catch(() => setData(null));
  }, [user?.id, user?.streak?.current]);

  if (!user) return null;
  const days = makeLast14Days();
  const set = new Set(data?.recent_dates || []);
  const current = data?.current ?? user.streak?.current ?? 0;
  const longest = data?.longest ?? user.streak?.longest ?? 0;
  const todayLogged = data?.today_logged ?? user.streak?.today_logged;

  return (
    <div className="mb-6 rounded-2xl nb-border nb-shadow bg-white p-4 md:p-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-center" data-testid="streak-strip">
      <div className="flex items-center gap-3">
        <motion.div
          animate={{ scale: [1, 1.15, 1], rotate: [0, -8, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className={`w-14 h-14 rounded-full nb-border grid place-items-center ${current > 0 ? "bg-orange-100" : "bg-neutral-100"}`}
        >
          <Flame className={`w-8 h-8 ${current > 0 ? "text-orange-500 fill-orange-300" : "text-neutral-400"}`} />
        </motion.div>
        <div>
          <div className="font-heading font-black text-3xl tracking-tighter leading-none">{current}<span className="text-base font-mono-sl text-neutral-500 ml-1">day{current === 1 ? "" : "s"}</span></div>
          <div className="font-mono-sl text-[10px] uppercase text-neutral-600 mt-0.5">
            Current streak · longest {longest}d
            {todayLogged && current > 0 && <span className="ml-2 text-green-700">✔ today done</span>}
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="flex items-end gap-1.5" data-testid="streak-heat">
          {days.map((d) => {
            const on = set.has(d);
            const isToday = d === (data?.today || days[days.length - 1]);
            return (
              <div key={d} title={d} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-full h-6 rounded-md nb-border ${on ? "bg-orange-400" : "bg-neutral-100"} ${isToday ? "ring-2 ring-black" : ""}`} />
                <div className="font-mono-sl text-[9px] text-neutral-500">{d.slice(-2)}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-2 font-mono-sl text-[10px] uppercase text-neutral-700">{encouragementFor(current)}</div>
        {data?.unlocked?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="streak-badges">
            {data.unlocked.map((m) => (
              <div key={m.id} title={m.message}
                className={`flex items-center gap-1 px-2 py-1 rounded-full nb-border font-mono-sl text-[10px] uppercase bg-sl-${m.color}`}>
                <span>{m.emoji}</span> <span>{m.name}</span>
              </div>
            ))}
          </div>
        )}
        {data?.next_milestone && (
          <div className="mt-1 font-mono-sl text-[10px] uppercase text-neutral-500">
            Next: <span className="text-neutral-800">{data.next_milestone.emoji} {data.next_milestone.name}</span> — {data.next_milestone.days - current} day{data.next_milestone.days - current === 1 ? "" : "s"} to go
          </div>
        )}
      </div>
    </div>
  );
}
